import { Box, FormControlLabel, IconButton, Popover } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { meetingSocketFactory } from "../../../../utils/socketFactory";
import IOSSwitch from "../../../../components/IOSSwitch";
import { useDispatch, useSelector } from "react-redux";
import AddIconSvg from "../../../../assets/add.svg";
import MoodIconSvg from "../../../../assets/mood.svg";
import SendIconSvg from "../../../../assets/send.svg";
import PageSvg from "../../../../assets/Page.svg";
import { useTranslation } from "react-i18next";
import EmojiPicker from "emoji-picker-react";
import "./ConferenceMessaging.css";
import { getConferenceUserParams } from "../../../../utils/conferenceUtils";
import { sendConferenceMessage } from "../../store/ActiveConferenceThunks";
import { USER_TYPING } from "../../store/ActiveConferenceSlice";


function linkify(text) {
    if (!text) return text;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.replace(urlRegex, (url) => {
        return `<span class="mh-link-wrapper"><a href="${url}" target="_blank" rel="noopener noreferrer" class="mh-link">${url}</a></span>`;
    });
}

const formatBytes = (bytes) => {
    if (bytes === undefined || bytes === null) return "";
    const units = ["B", "KB", "MB", "GB"];
    let i = 0;
    let n = bytes;
    while (n >= 1024 && i < units.length - 1) {
        n = n / 1024;
        i++;
    }
    return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const MessageAttachment = ({ file_name, file_type, file_url, file_size }) => {
    const getExt = (file_name, file_type) => {
        const fromName = file_name?.split(".").pop();
        if (fromName) return fromName.toUpperCase();
        const map = {
            "application/pdf": "PDF",
            "application/msword": "DOC",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
            "text/csv": "CSV",
            "application/vnd.ms-excel": "XLS",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
            "application/vnd.ms-powerpoint": "PPT",
            "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PPTX",
            "application/zip": "ZIP",
            "application/x-zip-compressed": "ZIP",
            "image/jpeg": "JPG",
            "image/png": "PNG",
            "image/webp": "WEBP",
            "image/gif": "GIF",
            "image/tiff": "TIFF",
        };
        return map[file_type] || "FILE";
    };

    const ext = getExt(file_name, file_type);
    const FILE_BADGE = {
        PDF: { bg: "#D92D20" },
        DOC: { bg: "#155EEF" },
        DOCX: { bg: "#155EEF" },
        XLS: { bg: "#079455" },
        XLSX: { bg: "#079455" },
        PPT: { bg: "#F79009" },
        PPTX: { bg: "#F79009" },
        CSV: { bg: "#079455" },
        JPG: { bg: "#479A32" },
        PNG: { bg: "#479A32" },
        WEBP: { bg: "#479A32" },
        GIF: { bg: "#479A32" },
        TIFF: { bg: "#479A32" },
        ZIP: { bg: "#414651" },
    };
    const badgeBg = (FILE_BADGE[ext]?.bg) || "#E5E7EB";

    return (
        <a
            href={file_url}
            download={file_name}
            target="_blank"
            rel="noopener noreferrer"
            className="mh-attach"
            style={{ width: "100%" }}
        >
            <div className="mh-attach__iconWrap">
                <img src={PageSvg} alt="file" className="mh-attach__icon" />

                <div
                    className="mh-attach__badge"
                    style={{ "--badge-bg": badgeBg }}
                >
                    {ext}
                </div>
            </div>
            <div className="mh-attach__meta"
            style={{"width" : "100%" }}>
                <span className="mh-attach__name"
                    style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap", // Required for ellipsis to work
                        color: "var(--colors-text-text-secondary-700, #414651)",
                        fontSize: "0.875rem",
                        fontStyle: "normal",
                        fontWeight: 500,
                        lineHeight: "normal",
                        maxWidth: "80%",
                    }}>{file_name}</span>
                <span className="mh-attach__sub">{file_size ? formatBytes(file_size) : file_type || ""}</span>
            </div>
        </a>
    );
};

const UsersTyping = ({ usersTyping }) => {
    const containerRef = useRef(null);
    const testRef = useRef(null);
    const { t } = useTranslation()

    const getFirstName = (username) => username.split(" ")[0];

    const measureText = (text) => {
        testRef.current.textContent = text;
        return testRef.current.scrollWidth;
    };

    const truncateName = (name, suffix, maxWidth) => {
        let truncated = name;
        while (truncated.length > 0) {
            const candidate = `${truncated}...${suffix}`;
            if (measureText(candidate) <= maxWidth) return candidate;
            truncated = truncated.slice(0, -1);
        }
        return `...${suffix}`;
    };

    const getTruncatedText = (users) => {
        if (users.length === 0) return "";

        const containerWidth = containerRef.current?.offsetWidth ?? 0;

        if (users.length === 1) {
            const name = getFirstName(users[0].username);

            // Step 1: Try full verb phrase
            const full = `${name} ${t('is_typing')}...`;
            if (measureText(full) <= containerWidth) return full;

            // Step 2: Try short verb phrase
            const short = `${name} ${t('is_typing_short')}...`;
            if (measureText(short) <= containerWidth) return short;

            // Step 3: Truncate name with short verb phrase
            return truncateName(name, ` ${t('is_typing_short')}...`, containerWidth);
        }

        // Try progressively fewer names
        for (let shown = users.length; shown >= 1; shown--) {
            const names = users.slice(0, shown).map(u => getFirstName(u.username)).join(", ");
            const othersCount = users.length - shown;

            // Step 1: Try full verb phrase
            const full = othersCount > 0
                ? `${names} ${t('and')} ${othersCount} ${t('others')} ${t('are_typing')}...`
                : `${names} ${t('are_typing')}...`;
            if (measureText(full) <= containerWidth) return full;

            // Step 2: Try short verb phrase
            const short = othersCount > 0
                ? `${names} ${t('and')} ${othersCount} ${t('others')} ${t('are_typing_short')}...`
                : `${names} ${t('are_typing_short')}...`;
            if (measureText(short) <= containerWidth) return short;
        }

        // Step 3: Truncate the single name with short verb phrase
        const firstName = getFirstName(users[0].username);
        const suffix = users.length > 1
            ? ` ${t('and')} ${users.length - 1} ${t('others')} ${t('are_typing_short')}...`
            : ` ${t('is_typing_short')}...`;
        return truncateName(firstName, suffix, containerWidth);
    };

    return (
        <div className="users-typing-container" ref={containerRef}>
            <span
                ref={testRef}
                style={{ visibility: "hidden", position: "absolute", whiteSpace: "nowrap" }}
            />
            <span style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
                {getTruncatedText(usersTyping)}
            </span>
        </div>
    );
};

export default function ConferenceMessaging() {

    const { t } = useTranslation();
    const { localAvatar } = useSelector((state) => state.auth);
    const { currentRoom } = useSelector((state) => state.MapDisplay);
    const { uiState, conference } = useSelector((state) => state.activeConference);
    const avatarsData = useSelector((state) => state.avatars?.avatars || {});
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const inputRef = useRef(null);
    const [messageText, setMessageText] = useState("");

    const [isUploading, setIsUploading] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    const [emojiAnchor, setEmojiAnchor] = useState(null);
    const [showActions, setShowActions] = useState(true);
    const emojiOpen = Boolean(emojiAnchor);
    const messagesEndRef = useRef(null);

    const { joinedParticipants } = getConferenceUserParams(
        conference,
        avatarsData,
        localAvatar,
        uiState.is_conference_active
    );

    const [usersTyping, setUsersTyping] = useState([]);



    useEffect(() => {
        const cleanupUserTyping = (userId) => {
            setUsersTyping((prev) => {
                return prev.filter(u => u.userId !== userId)
            })
        }

        const addOrUpdateUserTyping = (userId, username, cleanupTimeout) => {
            setUsersTyping(prev => {
                const existing = prev.find(u => u.userId === userId);

                if (existing) {
                    if (existing.cleanupTimeout !== cleanupTimeout) {
                        clearTimeout(existing.cleanupTimeout);
                    }
                    return prev.map(u =>
                        u.userId === userId
                            ? { ...u, cleanupTimeout }
                            : u
                    );
                } else {
                    return [...prev, { userId, username, cleanupTimeout }];
                }
            });
        };
        const callback = meetingSocketFactory.on('userTyping', (payload) => {
            const data = payload.data;
            const cleanupTimeout = setTimeout(() => {
                cleanupUserTyping(data.userId)
            }, 8 * 1000);
            addOrUpdateUserTyping(data.userId, data.username, cleanupTimeout)
        })
        return () => {
            meetingSocketFactory.off(callback)
        }
    }, [])

    useEffect(() => { console.log('users typing changed:', usersTyping) }, [usersTyping])

    const transformMessages = (messages, joinedParticipants, conferenceParticipants) => {
        if (!messages || !Array.isArray(joinedParticipants) || !Array.isArray(conferenceParticipants)) {
            return [];
        }

        const connectedUserIds = new Set(joinedParticipants.map(p => p.user_id));
        const participantByUserId = new Map(
            conferenceParticipants.map(p => [p.user_id, p])
        );

        return messages
            .map(msg => {
                const senderUser = msg.sender?.user || {};
                const joinedParticipant = joinedParticipants.find(p => p.user_id === senderUser.id);
                const conferenceParticipant = !joinedParticipant
                    ? participantByUserId.get(senderUser.id)
                    : null;

                const hasPhoto = senderUser.urlphoto;
                const initials = joinedParticipant?.initials
                    || (!hasPhoto && senderUser.firstName && senderUser.lastName
                        ? `${senderUser.firstName[0]}${senderUser.lastName[0]}`.toUpperCase()
                        : "?");

                const participantIsConnected = connectedUserIds.has(senderUser.id);
                const participantRole = joinedParticipant?.role || conferenceParticipant?.role || "guest";
                const participantPhoto = senderUser.urlphoto || null;

                return {
                    id: `msg_${msg.id}`,
                    type: "message",
                    content: msg.message,
                    fileUrl: msg.file_url,
                    fileName: msg.file_name,
                    fileType: msg.file_type,
                    fileSize: msg.file_size,
                    participantId: msg.sender_id,
                    participantName: `${senderUser.firstName || ""} ${senderUser.lastName || ""}`.trim() || senderUser.email || "Utilisateur",
                    participantInitials: initials,
                    participantPhoto,
                    participantRole,
                    participantIsConnected,
                    messageDate: msg.timestamp
                };
            })
            .sort((a, b) => new Date(a.messageDate) - new Date(b.messageDate));
    };

    const transformActions = (actions, joinedParticipants, conferenceParticipants) => {
        if (!actions || !Array.isArray(joinedParticipants) || !Array.isArray(conferenceParticipants)) {
            return [];
        }

        return actions
            .map(action => {
                const actionUser = action.user || {};
                return {
                    id: `act_${action.id}`,
                    type: "action",
                    content: action.description,
                    participantId: action.user_id,
                    participantName: `${actionUser.firstName || ""} ${actionUser.lastName || ""}`.trim() || actionUser.email || "Utilisateur",
                    messageDate: action.date
                };
            });
    };

    const sortedMessages = useMemo(() => {
        const rawMessages = conference?.conversation?.messages || [];
        const rawActions = showActions ? (conference?.actions || []) : [];
        const confParticipants = conference?.conferenceParticipants || [];
        const messages = transformMessages(rawMessages, joinedParticipants || [], confParticipants);
        const actions = transformActions(rawActions, joinedParticipants || [], confParticipants);

        return [...messages, ...actions].sort((a, b) => new Date(a.messageDate) - new Date(b.messageDate));
    }, [conference, joinedParticipants, showActions]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [sortedMessages.length]);

    const handleSend = async () => {
        const hasText = !!messageText.trim();
        const hasFile = !!pendingFile;

        if (!hasText && !hasFile) return;

        try {
            setIsUploading(true);

            const formData = new FormData();
            formData.append("senderEmail", localAvatar.email);
            formData.append("conversationId", conference?.conversation?.id);
            formData.append("conferenceId", conference.id);

            if (hasText) {
                formData.append("text", messageText.trim());
            }

            if (hasFile) {
                formData.append("file", pendingFile);
            }

            await dispatch(sendConferenceMessage({ body: formData })).unwrap();



            setPendingFile(null);
            setMessageText("");
            return;

        } catch (err) {
            console.error("Error sending conference message:", err);
        }
        finally {
            setIsUploading(false);
        }
    };
    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setPendingFile(file);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const getAvatarStyle = (role) => {
        if (role === "principal-master") return { backgroundColor: "#7C3BED", color: "white", padding: "0.25rem" };
        if (role === "master") return { backgroundColor: "#2662D9", color: "white", padding: "0.25rem" };
        return { backgroundColor: "#E3E8EF", color: "#4A5568", padding: "0.25rem" };
    };

    const getBadgeClass = (role) => {
        if (role === "principal-master") return "badge-master-principal";
        if (role === "master") return "badge-master";
        return "badge-default";
    };

    return (
        <div className="messaging-container">
            <div className="messaging-header">
                <div className="messaging-head">
                    <div className="messaging-head-left">
                        <div className="material-symbols-outlined messaging-icon">
                            videocam
                        </div>
                        <div className="messaging-title">
                            <span className="messaging-room-label">
                                salle {currentRoom.label}
                            </span>
                            <span className="messaging-label">{t('chat_saved_at_the_end')} </span>
                        </div>
                    </div>
                    <div className="messaging-head-right">
                        <div className="messaging-live">
                            <span className="messaging-live-label">
                                <span className="live-dot"></span>
                                {t('live')}
                            </span>
                        </div>
                        <div className="messaging-actions">
                            <div className="messaging-action-toggle">
                                <FormControlLabel
                                    control={
                                        <Box sx={{
                                            width: 38,
                                            height: 16,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <IOSSwitch
                                                colorvariant="standard"
                                                checked={showActions}
                                                onChange={(e) => { setShowActions(e.target.checked) }}
                                                sx={{
                                                    transform: 'scale(0.65)',
                                                    transformOrigin: 'center center'
                                                }}
                                            />
                                        </Box>
                                    }
                                    sx={{ margin: "0", display: "flex", alignItems: "center", gap: "0" }}
                                />
                            </div>
                            <span className="messaging-action-label">
                                {t('display_actions')}
                            </span>

                        </div>
                    </div>
                </div>
            </div>
            <div className="messaging-body">
                <div className="messaging-content">
                    {sortedMessages.map((msg) => (
                        msg.type === "message" ? (
                            <div key={msg.id} className="message-row">
                                <div className="msg-participant"
                                    style={msg.participantPhoto ? {} : getAvatarStyle(msg.participantRole)}>
                                    {msg.participantPhoto ? (
                                        <img src={msg.participantPhoto} alt={msg.participantName} className="participant-avatar-img" />
                                    ) : (
                                        <span className="participant-avatar-initials">{msg.participantInitials}</span>
                                    )}
                                    {msg.participantIsConnected && <span className="online-dot" />}
                                </div>
                                <div className="message-wrapper">
                                    <div className="message-meta">
                                        <div className="message-participant">
                                            <span className="msg-name">{msg.participantName}</span>
                                            <span className={`msg-role ${getBadgeClass(msg.participantRole)}`}>
                                                {msg.participantRole === "principal-master" && (
                                                    <svg className="crown-icon-principal" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7z"></path>
                                                    </svg>
                                                )}
                                                {msg.participantRole === "master" && (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <path d="M13.3336 8.66664C13.3336 12 11.0003 13.6666 8.22699 14.6333C8.08176 14.6825 7.92401 14.6802 7.78032 14.6266C5.00032 13.6666 2.66699 12 2.66699 8.66664V3.99997C2.66699 3.82316 2.73723 3.65359 2.86225 3.52857C2.98728 3.40355 3.15685 3.33331 3.33366 3.33331C4.66699 3.33331 6.33366 2.53331 7.49365 1.51997C7.63489 1.39931 7.81456 1.33301 8.00032 1.33301C8.18608 1.33301 8.36575 1.39931 8.50699 1.51997C9.67365 2.53997 11.3337 3.33331 12.667 3.33331C12.8438 3.33331 13.0134 3.40355 13.1384 3.52857C13.2634 3.65359 13.3336 3.82316 13.3336 3.99997V8.66664Z" stroke="#005bdb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                )}
                                            </span>
                                        </div>
                                        <span className="msg-time">
                                            {(() => {
                                                const d = new Date(msg.messageDate);
                                                return `${d.getHours().toString().padStart(2, '0')}h${d.getMinutes().toString().padStart(2, '0')}`;
                                            })()}
                                        </span>
                                    </div>
                                    <div className="msg-content">
                                        {msg.fileUrl && (
                                            <div className="msg-attachment">
                                                <MessageAttachment
                                                    file_name={msg.fileName}
                                                    file_type={msg.fileType}
                                                    file_url={msg.fileUrl}
                                                    file_size={msg.fileSize}
                                                />
                                            </div>
                                        )}

                                        {msg.content && msg.content !== '[file]' && (
                                            <p
                                                className="msg-text"
                                                dangerouslySetInnerHTML={{ __html: linkify(msg.content) }}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : msg.type === "action" ? (
                            <div key={msg.id} className="messaging-action-item">
                                <span className="messaging-action-span">
                                    {t(msg.content, { name: msg.participantName })}
                                </span>
                            </div>
                        ) : null
                    ))}
                    <div ref={messagesEndRef} />
                </div>
                <UsersTyping usersTyping={usersTyping} />
                <Box className="figma-chat-wrapper">

                    <Box className="figma-chat-inner center-figma-inner">

                        <IconButton className="figma-add-btn" component="label" disabled={isUploading}>
                            <img src={AddIconSvg} alt="add" className="figma-add-icon" />
                            <input ref={fileInputRef} type="file" hidden onChange={handleFileUpload} />
                        </IconButton>



                        <Box className="figma-input-wrapper">
                            {pendingFile && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 1,
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: "0.5rem",
                                        background: "#EEF2F6",
                                    }}
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                                        attach_file
                                    </span>

                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {pendingFile.name}
                                    </span>

                                    <IconButton size="small" onClick={() => setPendingFile(null)}>
                                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                                            close
                                        </span>
                                    </IconButton>
                                </Box>
                            )}

                            <input
                                ref={inputRef}
                                type="text"
                                className="figma-input"
                                placeholder={t("message")}
                                value={messageText}
                                onChange={(e) => {
                                    setMessageText(e.target.value);
                                    dispatch(USER_TYPING())
                                }}
                                onKeyDown={handleKeyDown}
                                onFocus={() => dispatch(USER_TYPING())}
                            />

                            <img
                                src={MoodIconSvg}
                                alt="emoji"
                                className="figma-input-emoji-icon"
                                onClick={(e) => setEmojiAnchor(emojiOpen ? null : e.currentTarget)}
                            />
                        </Box>

                        <IconButton
                            className="figma-send-btn messaging-icon-send"
                            onClick={handleSend}
                            disabled={isUploading || (!messageText.trim() && !pendingFile)}
                        >
                            <img src={SendIconSvg} alt="send" className="figma-send-icon" />
                        </IconButton>

                    </Box>

                    <Popover
                        open={emojiOpen}
                        anchorEl={emojiAnchor}
                        onClose={() => setEmojiAnchor(null)}
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                    >
                        <EmojiPicker
                            onEmojiClick={(emojiData) => {
                                setMessageText((prev) => prev + emojiData.emoji);
                                setEmojiAnchor(null);
                                requestAnimationFrame(() => inputRef.current?.focus());
                            }}
                        />
                    </Popover>

                </Box>
            </div>
        </div>
    );
}